import apiClient from './api';

export const getProducts = (params = {}) => {
  // Ensure we always have a page_size parameter
  const finalParams = {
    page_size: 20,  // Default page size
    ...params       // But allow override from caller
  };
  console.log('Fetching products with params:', finalParams);
  return apiClient.get('/product/products/', { params: finalParams });
};

export const getProductsAutocomplete = (query = '') => {
  const params = query ? { q: query } : {};
  console.log('Fetching products autocomplete with params:', params);
  return apiClient.get('/product/products/autocomplete/', { params });
};

export const searchProducts = (query, page = 1, pageSize = 20, state = null) => {
  const params = {
    q: query,
    page: page,
    page_size: pageSize,
  };
  
  // Add state filter if provided and not 'all'
  if (state && state !== 'all') {
    params.state = state;
  }
  
  console.log('Searching products with params:', params);
  
  return apiClient.get(`/product/products/search/`, { params });
};

export const getProductVariants = (params = {}) => {
  // Ensure we always have a page_size parameter
  const finalParams = {
    page_size: 20,  // Default page size
    ...params       // But allow override from caller
  };
  console.log('Fetching variants with params:', finalParams);
  return apiClient.get('/product/variants/', { params: finalParams });
};

export const searchProductVariants = (query, page = 1, pageSize = 20, state = null, cacheBuster = null, ordering = null) => {
  const params = { 
    q: query,
    page: page,
    page_size: pageSize,
    // You can add more params here like category, color, size
  };
  
  // Add state filter if provided and not 'all'
  if (state && state !== 'all') {
    params.state = state;
  }
  
  // Add ordering if provided
  if (ordering) {
    params.ordering = ordering;
  }
  
  // Add cache buster if provided
  if (cacheBuster) {
    params._t = cacheBuster;
  }
  
  console.log('Searching variants with params:', params);
  
  return apiClient.get(`/product/variants/search/`, { params });
};

export const getCategories = (params = {}) => {
  // These endpoints are not paginated
  return apiClient.get('/product/categories/', { params });
};

export const getColors = (params = {}) => {
  // These endpoints are not paginated
  return apiClient.get('/product/colors/', { params });
};

export const getSizes = (params = {}) => {
  // These endpoints are not paginated
  return apiClient.get('/product/sizes/', { params });
};

export const getBins = (params = {}) => {
  // These endpoints are not paginated
  return apiClient.get('/product/bins/', { params });
};

export const getVendors = (params = {}) => {
  // These endpoints are not paginated
  return apiClient.get('/product/vendors/', { params });
};

// Product operations
export const createProduct = (productData) => {
  return apiClient.post('/product/products/', productData);
};

export const updateProduct = (id, productData) => {
  return apiClient.put(`/product/products/${id}/`, productData);
};

export const updateProductLocation = (productId, locationId) => {
  return apiClient.patch(`/product/products/${productId}/`, {
    location: locationId
  });
};

export const deleteProduct = (id) => {
  return apiClient.delete(`/product/products/${id}/`);
};

/**
 * Upload products or variants from CSV/Excel file
 * @param {FormData} formData - Form data containing the file and type (products or variants)
 * @returns {Promise} - Promise with the response
 */
export const uploadProductsFile = (formData) => {
  return apiClient.post('/product/upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * Download a template for product or variant uploads
 * @param {string} type - Type of template ('products' or 'variants')
 * @param {string} fmt - Format of the template ('csv' or 'xlsx')
 * @returns {Promise} - Promise with the response
 */
export const downloadProductTemplate = (type = 'products', fmt = 'xlsx') => {
  return apiClient.get('/product/download-template/', {
    params: { type, fmt },
    responseType: 'blob'
  });
};

/**
 * Legacy function for downloading variant template
 * @returns {Promise} - Promise with the response
 */
export const downloadVariantTemplate = () => {
  return downloadProductTemplate('variants', 'xlsx');
};

// Product Variant operations
export const createProductVariant = (variantData) => {
  return apiClient.post('/product/variants/', variantData);
};

export const updateProductVariant = (id, variantData) => {
  // Log the form data being sent for debugging
  if (variantData instanceof FormData) {
    console.log('Updating variant with FormData:');
    for (let [key, value] of variantData.entries()) {
      console.log(`- ${key}: ${value instanceof File ? value.name : value}`);
    }
    // When using FormData, don't set Content-Type header
    // The browser will set it automatically with the correct boundary
    return apiClient.put(`/product/variants/${id}/`, variantData);
  } else {
    // Handle JSON data case (legacy)
    return apiClient.put(`/product/variants/${id}/`, variantData);
  }
};

/**
 * Export model data as CSV or Excel file
 * @param {string} model - The model name to export (Product, ProductVariant, etc.)
 * @param {string} format - The export format ('csv' or 'xlsx')
 * @param {object} filters - Optional filters to apply to the data
 * @returns {Promise} - Axios promise with responseType blob
 */
export const exportModelData = (model, format = 'csv') => {
  // Build query parameters
  const params = {
    model: model,
    fmt: format  // Using fmt instead of format to avoid issues with URL encoding
  };
    // Return promise with responseType blob for file download
  return apiClient({
    url: '/product/export/',
    method: 'GET',
    params: params,
    responseType: 'blob' // Important for file download
  });
};

export const getProductsByBin = (binName) => {
  return apiClient.get(`/product/bins/products/`, { params: { bin_name: binName } });
};

// Vendor Statement API functions
export const getVendorStatements = (vendorId, searchParams = {}) => {
  const params = { ...searchParams };
  if (vendorId) {
    params.vendor = vendorId;
  }
  
  // Log the request for debugging
  console.log('Fetching vendor statements with params:', params);
  
  return apiClient.get('/product/vendor-statements/', { params })
    .then(response => {
      // Ensure we're returning an array or object with results array
      console.log('Vendor statements raw response:', response);
      
      // Handle both paginated and non-paginated responses
      if (response.data && !Array.isArray(response.data) && !response.data.results) {
        // If data is an object but doesn't have results property, wrap it in an array
        if (typeof response.data === 'object' && response.data !== null) {
          console.warn('Vendor statements response is an object, not an array. Converting to array.');
          response.data = [response.data];
        } else {
          console.warn('Unexpected vendor statements response format. Setting to empty array.');
          response.data = [];
        }
      }
      
      return response;
    })
    .catch(error => {
      console.error('Error fetching vendor statements:', error);
      // Re-throw to be handled by the component
      throw error;
    });
};

export const createVendorStatement = (statementData) => {
  return apiClient.post('/product/vendor-statements/', statementData);
};

export const updateVendorStatement = (id, statementData) => {
  return apiClient.put(`/product/vendor-statements/${id}/`, statementData);
};

export const deleteVendorStatement = (id) => {
  return apiClient.delete(`/product/vendor-statements/${id}/`);
};

export const getVendorBalance = (vendorId) => {
  return apiClient.get(`/product/vendor-statements/balance/${vendorId}/`);
};

export const downloadVendorStatementsLast365Days = (vendorId) => {
  return apiClient.get(`/product/vendor-statements/export-last-60-days/${vendorId}/`, {
    responseType: 'blob'
  });
};

// Re-export apiClient for backward compatibility
export { default as apiClient } from './api';

// Inventory Transaction endpoints
export const getTransactions = (params = {}) => {
  const finalParams = {
    page_size: 20,  // Default page size
    ...params
  };
  return apiClient.get('/product/inventory-transactions/', { params: finalParams });
};



export const createTransaction = (transactionData) => {
  return apiClient.post('/product/inventory-transactions/', transactionData);
};

export const deleteTransaction = (id) => {
  return apiClient.delete(`/product/inventory-transactions/${id}/`);
};

export const getTransactionById = (id) => {
  return apiClient.get(`/product/inventory-transactions/${id}/`);
};

export const findVariantByExactSku = (sku) => {
  return apiClient.get(`/product/variants/find_by_sku/?sku=${sku}`);
};

// Pick List operations
export const getPickList = (params = {}) => {
  const finalParams = {
    page_size: 50,  
    ...params
  };
  return apiClient.get('/product/inventory-transactions/pick_list/', { params: finalParams });
};

export const markItemAsPicked = (id) => {
  return apiClient.post(`/product/inventory-transactions/${id}/mark_picked/`);
};

export const getPickHistory = (params = {}) => {
  const finalParams = {
    page_size: 50,
    ...params
  };
  return apiClient.get('/product/inventory-transactions/pick_history/', { params: finalParams });
};

export const getHoldList = (params = {}) => {
  const finalParams = {
    page_size: 50,
    ...params
  };
  return apiClient.get('/product/inventory-transactions/hold_list/', { params: finalParams });
};

export const markItemAsUnpicked = (id) => {
  return apiClient.post(`/product/inventory-transactions/${id}/mark_unpicked/`);
};

export const markItemAsHold = (id) => {
  return apiClient.post(`/product/inventory-transactions/${id}/mark_hold/`);
};

export const getLocations = () => {
  // Locations are not paginated
  return apiClient.get('/product/bins/locations/');
};
